/* eslint-disable @typescript-eslint/no-explicit-any */
import './../../assets/scss/components/_modified-datepicker.scss'

import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import {
  FormatDateHelper,
  dateFormatPerLocale,
} from '../../utils/dateFormatUtils'
import { openToDateHandler } from '../../utils/datePicketUtils'

const ModifiedDatePicker = (props: {
  id?: string
  className?: string
  name?: string
  value?: string
  dataTestId?: string
  onChange?(
    date: Date | null,
    dateString?: string,
    event?: React.SyntheticEvent<unknown>,
    dateArray?: [Date | null, Date | null],
  ): void
  placeholderText?: string
  dropdownMode?: 'scroll' | 'select'
  maxDate?: Date | null
  minDate?: Date | null
  showClearButton?: boolean
  allowBackspace?: boolean
  autoComplete?: string
  disabled?: boolean
  peekNextMonth?: boolean
  showMonthDropdown?: boolean
  showYearDropdown?: boolean
  disabledKeyboardNavigation?: boolean
  readOnly?: boolean
  openToDate?: Date | string
  selected?: Date | string | null
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void
  onCalendarClose?(): void
  onCalendarOpen?(): void
  onYearChange?(date: Date): void
  onMonthChange?(date: Date): void
  onInputClick?(): void
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLDivElement>): void
  onSelect?(date: Date, event: React.SyntheticEvent<unknown> | undefined): void
}): JSX.Element => {
  const localeDateFormat = dateFormatPerLocale.find(
    (lang) => lang.label === navigator.languages[0],
  )

  const datePickerRef = useRef(null)

  const dateFormat = localeDateFormat?.format || 'yyyy-MM-dd'

  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [currentValue, setCurrentValue] = useState<string | undefined>('')

  const baseFormat = 'DD/MM/YYYY'

  const onOverrideChange = (
    date: Date,
    event?: React.SyntheticEvent<unknown> | undefined,
  ) => {
    const isFormatted = FormatDateHelper(date)

    if (!date) {
      console.warn(
        'Date is empty. Please wrap your date value using FormatDateHelper to avoid from crashing the page.',
      )
    }

    if (props?.onChange) props?.onChange(date, isFormatted, event)
  }

  useEffect(() => {
    const hasNumericalEquivalent = isNaN(Number(props?.selected))

    if (props?.selected instanceof Date && hasNumericalEquivalent) {
      setSelectedDate(props?.selected)
      return
    }

    if (
      typeof props?.selected === 'string' &&
      props?.selected === 'Invalid Date'
    ) {
      setSelectedDate(new Date())
    }
  }, [props?.selected])

  useEffect(() => {
    if (props?.value) {
      if (dateFormat?.toLowerCase().startsWith('d')) {
        setCurrentValue(props.value)
        return
      }

      const d = props?.value.split('/')
      setCurrentValue(`${d[1]}/${d[0]}/${d[2]}`)
      return
    }

    setCurrentValue('')
  }, [props?.value])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const datepickerElement: any = datePickerRef.current

    if (props?.allowBackspace && event.key === 'Backspace') {
      datepickerElement.clear()
      onOverrideChange(new Date(''))
    } else {
      event.preventDefault()
    }
  }
  const currentYear = new Date().getFullYear()
  const fixedMinDate = new Date(2000, 0, 1)
  const fixedMaxDate = new Date(currentYear + 1, 11, 31)

  const shouldApplyYearRange =
    props?.showYearDropdown && !props?.minDate && !props?.maxDate

  const effectiveMinDate = shouldApplyYearRange ? fixedMinDate : props?.minDate

  const effectiveMaxDate = shouldApplyYearRange ? fixedMaxDate : props?.maxDate

  return (
    <DatePicker
      ref={datePickerRef}
      isClearable={true}
      id={props?.id}
      data-testid={props?.dataTestId}
      clearButtonClassName={
        props?.showClearButton
          ? 'react-datepicker__close-icon2'
          : 'react-datepicker__hide-clear-btn'
      }
      className={props?.className}
      name={props?.name}
      value={currentValue}
      onChange={onOverrideChange}
      placeholderText={
        props?.placeholderText
          ? props?.placeholderText
          : dateFormat?.toLowerCase()
      }
      dateFormat={dateFormat}
      dropdownMode={props?.dropdownMode}
      maxDate={effectiveMaxDate}
      minDate={effectiveMinDate}
      autoComplete={props?.autoComplete}
      disabled={props?.disabled}
      readOnly={props?.readOnly}
      peekNextMonth={props?.peekNextMonth}
      showMonthDropdown={props?.showMonthDropdown}
      showYearDropdown={props?.showYearDropdown}
      disabledKeyboardNavigation={props?.disabledKeyboardNavigation}
      openToDate={
        props?.openToDate instanceof Date
          ? props?.openToDate
          : openToDateHandler(props?.openToDate as string)
      }
      highlightDates={[
        {
          'today-date-highlight': [
            props?.value
              ? moment(props?.value, baseFormat).toDate()
              : new Date(),
          ],
        },
        { 'today-date-highlight': [new Date()] },
      ]}
      selected={selectedDate}
      onBlur={props?.onBlur}
      onCalendarClose={props?.onCalendarClose}
      onCalendarOpen={props?.onCalendarOpen}
      onYearChange={props?.onYearChange}
      onMonthChange={props?.onMonthChange}
      onInputClick={props?.onInputClick}
      onFocus={props?.onFocus}
      onKeyDown={(e) => {
        if (props?.onKeyDown) props.onKeyDown(e)
        handleKeyDown(e)
      }}
      onSelect={props?.onSelect}
    />
  )
}

export default ModifiedDatePicker
